import { Button } from 'devextreme-react';
import { Column, DataGrid, FilterRow, Grouping, GroupItem, GroupPanel, LoadPanel, Lookup, MasterDetail } from 'devextreme-react/data-grid';
import * as ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Card } from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import ErrorBoundary from '../../components/common/ErrorBoundary';
import CancelTransferModal from '../../components/modals/CancelTransferModal';
import api from '../../services/api-configuration';
import useFormatMessage from '../../utils/useFormatMessage';

const DepolarArasiTransferIzleme = () => {
    const [dataSource, setDataSource] = useState([]);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [apiResponse, setApiResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [chassisList, setChassisList] = useState([]);
    const { t } = useFormatMessage();
    const { hasRole } = useAuth(22);

    useEffect(() => {
        getAlldata();
    }, []);

    const getAlldata = async () => {
        try {
            const response = await api.post("/getWarehouseTransferList");
            setDataSource(response.data.data || []);
        } catch (error) {
            console.error("Depolar arası transfer listesi getirilemedi.");
        }
    };

    const handleCloseModal = useCallback(() => {
        if (isLoading) return;
        setCancelModalOpen(false);
    }, [isLoading]);

    const handleCancelTransfer = useCallback(async () => {
        if (!selectedFile || isLoading) return;

        try {
            //  setIsLoading(true);
            setApiResponse(null);

            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('user_id',
                parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'))
            );

            const response = await api.post("/cancelWarehouseTransfer", formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setApiResponse(response.data);

            if (response.data.success && (!response.data.stats || response.data.stats.failed === 0)) {
                await getAlldata();
                setSelectedFile(null);
                handleCloseModal();
            }
        } catch (error) {
            console.error("Transfer iptali başarısız oldu:", error);
            setApiResponse({
                success: false,
                message: "İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin."
            });
        } finally {
            setIsLoading(false);
        }
    }, [selectedFile, isLoading, getAlldata, handleCloseModal]);

    const handleAddChassis = useCallback((newChassis) => {
        setChassisList(prevList => {
            const uniqueNewChassis = newChassis.filter(chassis => !prevList.includes(chassis));
            return [...prevList, ...uniqueNewChassis];
        });
    }, []);

    const handleOpenCancelModal = useCallback((transfer) => {
        const waitingChassis = transfer.chassis_list
            .filter(chassis => chassis.status === "WAITING")
            .map(chassis => chassis.chassis_no);

        setSelectedFile(null);
        setChassisList(waitingChassis);
        setCancelModalOpen(true);
    }, []);

    const handleOpenBulkCancelModal = useCallback(() => {
        setSelectedFile(null);
        setApiResponse(null);
        setCancelModalOpen(true);
    }, []);

    const handleRemoveChassis = useCallback((chassisToRemove) => {
        setChassisList(prev => prev.filter(chassis => chassis !== chassisToRemove));
    }, []);

    const handleClearChassisList = useCallback(() => {
        setChassisList([]);
    }, []);

    const renderCancelButton = useCallback((data) => {
        const waitingChassisCount = data.data.chassis_list.filter(chassis => chassis.status === "WAITING").length;
        if (waitingChassisCount === 0) return null;

        return (
            <Button
                onClick={() => handleOpenCancelModal(data.data)}
                style={{
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    color: '#333',
                    padding: '3px 8px',
                    fontSize: '0.8rem'
                }}
            >
                İptal ({waitingChassisCount})
            </Button>
        );
    }, [handleOpenCancelModal]);

    const onExporting = useCallback((e) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Transfer List');

        // Headers
        const headers = ['Emir ID', 'Çıkış Depo', 'Varış Depo', 'Tarih', 'Kullanıcı', 'Durum'];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.alignment = { horizontal: 'center' };

        // Data
        dataSource.forEach((item) => {
            worksheet.addRow([
                item.emir_id,
                item.geldigi_depo,
                item.gidecegi_depo,
                new Date(item.tarih).toLocaleString(),
                item.kullanici,
                `${item.chassis_list.filter(sase => sase.status === "COMPLETED").length} / ${item.chassis_list.length}`
            ]);

            // Add chassis details
            worksheet.addRow(['', 'Şase No', 'Durum']);
            item.chassis_list.forEach(chassis => {
                worksheet.addRow(['', chassis.chassis_no, t(chassis.status)]);
            });
            worksheet.addRow([]); // Empty row for spacing
        });

        // Auto-fit columns
        worksheet.columns.forEach(column => {
            column.width = Math.min(
                Math.max(
                    ...worksheet.getColumn(column.number).values
                        .filter(value => value)
                        .map(value => value.toString().length)
                ) + 2,
                30
            );
        });

        // Save
        workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DepolarArasiTransfer.xlsx');
        });

        e.cancel = true;
    }, [dataSource, t]);

    return (
        <>
            <Breadcrumb current="Depolar Arası Transfer İzleme" />
            <CancelTransferModal
                isOpen={cancelModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleCancelTransfer}
                selectedFile={selectedFile}
                onFileSelect={setSelectedFile}
                apiResponse={apiResponse}
                isLoading={isLoading}
            />
            <ErrorBoundary>
                <Card className='p-4'>
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "8px",
                        marginBottom: "16px"
                    }}>
                        {
                            hasRole("depolararasi_emir_write") &&
                            <Button
                                onClick={handleOpenBulkCancelModal}
                                style={{
                                    backgroundColor: 'white',
                                    border: '1px solid #ff1111',
                                    color: '#ff1111',
                                    padding: '3px 8px',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Transfer Emri İptal
                            </Button>}
                        <Button
                            onClick={getAlldata}
                            style={{
                                backgroundColor: 'white',
                                border: '1px solid #dee2e6',
                                color: '#333',
                                padding: '3px 8px',
                                fontSize: '0.85rem'
                            }}
                        >
                            <i className="fa fa-refresh"></i>
                        </Button>
                        <Button
                            onClick={onExporting}
                            style={{
                                backgroundColor: '#21AF47',
                                border: '1px solid #dee2e6',
                                color: 'white',
                                padding: '3px 8px',
                                fontSize: '0.85rem'
                            }}
                        >
                            <i className="fa fa-file-excel-o"></i>
                        </Button>
                    </div>

                    <DataGrid
                        dataSource={dataSource}
                        showBorders={true}
                        columnAutoWidth={true}
                        columnHidingEnabled={true}
                        columnFixingEnabled={true}
                        columnResizingMode={"nextColumn"}
                        columnMinWidth={50}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showColumnLines={true}
                        style={{
                            height: "calc(100vh - 400px)"
                        }}
                    >
                        <LoadPanel enabled={true} text='Yükleniyor...' />
                        <FilterRow visible={true} />
                        <GroupPanel visible={true} emptyPanelText="Gruplamak için kolon başlığını buraya sürükleyin" />
                        <Grouping autoExpandAll={false}>
                            <GroupItem dataField="geldigi_depo" />
                            <GroupItem dataField="gidecegi_depo" />
                            <GroupItem dataField="kullanici" />
                        </Grouping>
                        <MasterDetail
                            enabled={true}
                            component={MasterDetailGrid}
                        />
                        <Column dataField="emir_id" caption="Emir ID" alignment='center' width={"130"} />
                        <Column dataField="geldigi_depo" caption="Çıkış Depo" alignment='center' />
                        <Column dataField="gidecegi_depo" caption="Varış Depo" alignment='center' />
                        <Column dataField="tarih" caption="Tarih" alignment='center'
                            dataType='datetime'
                            format={"dd/MM/yyyy HH:mm"}
                        />
                        <Column dataField="kullanici" caption="Kullanıcı" alignment='center' />
                        <Column caption="Durum" alignment='center'
                            width={220}
                            cellRender={(data) => {
                                const chassisList = data.data.chassis_list;
                                const completedCount = chassisList.filter(sase => sase.status === "COMPLETED").length;
                                const canceledCount = chassisList.filter(sase => sase.status === "CANCEL" || sase.status === "CANCELED").length;
                                const totalActiveCount = chassisList.length - canceledCount;
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2px' }} className='mx-2'>
                                        {canceledCount > 0 ? (
                                            <i
                                                className="fa fa-ban"
                                                style={{
                                                    color: '#ff1111',
                                                    fontSize: '15px',
                                                    cursor: 'pointer'
                                                }}
                                                title={`${canceledCount} şase iptal edildi`}
                                            />
                                        ) : (<i></i>)}
                                        <Badge style={{
                                            fontSize: "medium",
                                            color: "white"
                                        }}
                                            color={completedCount === totalActiveCount ? "success" : "secondary"}
                                            className='p-1 px-2'>
                                            {completedCount} / {totalActiveCount}
                                        </Badge>
                                    </div>
                                );
                            }}
                        />
                    </DataGrid>
                </Card>
            </ErrorBoundary>
        </>
    );
};

const MasterDetailGrid = ({ data }) => (
    <DataGrid
        dataSource={data.data?.chassis_list}
        showBorders={true}
        columnAutoWidth={true}
        columnHidingEnabled={true}
        columnFixingEnabled={true}
        columnResizingMode={"nextColumn"}
        columnMinWidth={50}
        showRowLines={true}
        showColumnLines={true}
        height={400}
        className='data-grid-header-gray data-grid-gray'
        rowAlternationEnabled={true}
        paging={{
            enabled: false
        }}
        scrolling={{
            mode: "infinite"
        }}
    >
        <FilterRow visible={true} />
        <Column dataField="chassis_no" caption="Şase" alignment='center' />
        <Column
            dataField="status"
            caption="Tamamlandı"
            alignment='center'
            calculateFilterExpression={(filterValue) => {
                if (["COMPLETED", "WAITING", "CANCEL", "CANCELED"].includes(filterValue)) {
                    return ["status", "=", filterValue];
                }
            }}
            cellRender={(data) => {
                const icons = {
                    COMPLETED: { color: "green", icon: "check" },
                    WAITING: { color: "orange", icon: "circle-o" },
                    CANCEL: { color: "red", icon: "times" },
                    CANCELED: { color: "red", icon: "ban" }
                };
                const status = data.data.status;
                const iconConfig = icons[status];

                return iconConfig ? (
                    <b color={iconConfig.color}>
                        <i
                            style={{ color: iconConfig.color, fontWeight: "bold" }}
                            className={`fa fa-${iconConfig.icon}`}
                        />
                    </b>
                ) : null;
            }}
        >
            <Lookup
                dataSource={[
                    { value: 'COMPLETED', name: 'Tamamlandı' },
                    { value: 'WAITING', name: 'Beklemede' },
                    { value: 'CANCEL', name: 'İptal' },
                    { value: 'CANCELED', name: 'İptal Edildi' }
                ]}
                valueExpr="value"
                displayExpr="name"
            />
        </Column>
    </DataGrid>
);

export default DepolarArasiTransferIzleme;